import { Route, Switch } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import { authenticate } from './store/actions/auth';
import HomePage from './common/components/HomePage';
import NotFoundPage from './common/components/NotFoundPage';
import Loading from './common/components/Loading';
import LoadingOverlay from './common/disableUi/LoadingOverlay';
import SelectLocationContainer from './locations/SelectLocationContainer';
import NoLocationsPage from './common/components/NoLocationsPage';
import React, { useEffect } from 'react';
import ErrorBoundary from './components/utils/ErrorBoundary';
import ManifestingContainer from './manifests/components/ManifestingContainer';
import ManifestEventLogContainer from './manifestEventLogs/components/ManifestEventLogContainer';
import ProtectedRoute from './common/components/ProtectedRoute';
import Header from './common/components/Header';
import { fullStoryIdentify } from './common/fullStory';
import { ModalProvider } from './contexts/modal-context';
import { CssLoader } from '@cimpress/react-components';
import { getCurrentLocation } from './store/actions/location';
import { nullLocation } from './store/reducers/location.reducer';
import { notFoundLocation } from './locations/locationsModels';
import ToastsContainer from './containers/utils/ToastsContainer';
import { UiOptions } from './store/state/ui-options';
import { defaultUiOptions } from './store/reducers/ui-options.reducer';
import { Profile } from './models/auth';
import { getFeatureFlags } from './store/actions/feature';
import Settings from './pages/Settings';
import { SelectPortal } from './contexts/select-portal';
import { FeatureRoute } from './components/utils/FeatureRoute';
import { Features } from './models/features';
import { StoreState } from './store/state';
import Shipments from './pages/Shipments';
import { ShipmentsExplorer } from './pages/ShipmentsExplorer';
import { Workflows } from './pages/Workflows';

interface Props {
  userProfile: Profile;
  userIsLoggedIn: boolean;
  authenticate: Function;
  isFetchingCurrentLocation: boolean;
  getCurrentLocation: Function;
  noLocationAvailable: boolean;
  uiOptions: UiOptions;
}

// tslint:disable-next-line:function-name
function App({
  userProfile,
  userIsLoggedIn,
  authenticate,
  isFetchingCurrentLocation,
  getCurrentLocation,
  noLocationAvailable,
  uiOptions,
}: Props) {
  const dispatch = useDispatch();

  useEffect(
    () => {
      if (!isFetchingCurrentLocation && !noLocationAvailable) {
        dispatch(getFeatureFlags.request());
      }
    },
    [noLocationAvailable, isFetchingCurrentLocation],
  );

  useEffect(
    () => {
      if (userIsLoggedIn) {
        getCurrentLocation();
        fullStoryIdentify(userProfile);
      } else {
        authenticate();
      }
    },
    [userIsLoggedIn],
  );

  let routes = [
    <Route exact path="/" component={HomePage} key="homeRoute" />,
    <ProtectedRoute
      exact path="/locations"
      component={SelectLocationContainer}
      userIsLoggedIn={userIsLoggedIn}
      key="locationsRoute"
    />,
    <ProtectedRoute
      exact path="/sortation"
      key="shipments"
      userIsLoggedIn={userIsLoggedIn}
      component={Shipments}
    />,
    <ProtectedRoute
      exact path="/manifests"
      component={ManifestingContainer}
      userIsLoggedIn={userIsLoggedIn}
      key="manifestsRoute"
    />,
    <ProtectedRoute
      exact path="/manifests/:manifestId/event-log"
      component={ManifestEventLogContainer}
      userIsLoggedIn={userIsLoggedIn}
      key="manifestEventLogRoute"
    />,
    <ProtectedRoute
      exact path="/explorer"
      component={ShipmentsExplorer}
      userIsLoggedIn={userIsLoggedIn}
      key="shipmentsExplorer"
    />,
    <FeatureRoute
      path="/workflows"
      whenFlag={Features.WORKFLOWS} isEnabled
      component={Workflows}
      key="workflows"
    />,
    <FeatureRoute
      whenFlag={Features.SHOW_SETTINGS}
      isEnabled
      path="/settings"
      key="settings"
      component={Settings}
    />,
    <Route component={NotFoundPage} key="notFoundRoute" />,
  ];

  if (!userIsLoggedIn || isFetchingCurrentLocation) {
    routes = [<Route component={Loading} key="loadingRoute" />];
  }
  if (noLocationAvailable) {
    routes = [<Route component={NoLocationsPage} key="noLocationsRoute" />];
  }

  return (
    <CssLoader>
      <SelectPortal>
        <ToastsContainer/>
        <ModalProvider>
          <ErrorBoundary>
            <Header />
            <div className="content">
              <LoadingOverlay
                showOverlay={uiOptions.locked}
              />
              <Switch>
                {routes}
              </Switch>
            </div>
          </ErrorBoundary>
        </ModalProvider>
      </SelectPortal>
    </CssLoader>
  );
}

const mapStateToProps = (state: StoreState) => ({
  userProfile: state.user.profile,
  userIsLoggedIn: state.user.isLoggedIn,
  isFetchingCurrentLocation: state.location === nullLocation,
  noLocationAvailable: state.location === notFoundLocation,
  uiOptions: state.uiOptions || defaultUiOptions,
});

const mapDispatchToProps = {
  authenticate: authenticate.request,
  getCurrentLocation: getCurrentLocation.request,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(App);
